import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { UserCircle, Settings, LogOut, User, Calculator, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdmin } from '../../hooks/useAdmin';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';

const UserMenu = () => {
  const { user, signOut } = useAuth();
  const { isAdmin } = useAdmin();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuRef, () => setIsOpen(false));

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsOpen(false);
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center space-x-4">
        <Link
          to="/login"
          className="btn btn-secondary"
        >
          Connexion
        </Link>
        <Link
          to="/register"
          className="btn btn-primary"
        >
          Inscription
        </Link>
      </div>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-full hover:bg-white/10 transition-colors"
      >
        <UserCircle className="h-6 w-6 text-white" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 z-50">
          <div className="px-4 py-2 border-b border-neutral-100">
            <p className="text-sm font-medium text-neutral-900">{user.email}</p>
          </div>

          <div className="py-1">
            <MenuLink
              to="/profile"
              icon={<User className="h-4 w-4" />}
              label="Mon profil"
              onClick={() => setIsOpen(false)}
            />
            <MenuLink
              to="/dashboard"
              icon={<Calculator className="h-4 w-4" />}
              label="Mes estimations"
              onClick={() => setIsOpen(false)}
            />
            <MenuLink
              to="/credits/purchase"
              icon={<CreditCard className="h-4 w-4" />}
              label="Acheter des crédits"
              onClick={() => setIsOpen(false)}
            />
            {isAdmin && (
              <MenuLink
                to="/admin"
                icon={<Settings className="h-4 w-4" />}
                label="Administration"
                onClick={() => setIsOpen(false)}
              />
            )}
          </div>

          <div className="border-t border-neutral-100 py-1">
            <button
              onClick={handleSignOut}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-neutral-50 flex items-center"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MenuLink = ({ 
  to, 
  icon, 
  label, 
  onClick 
}: { 
  to: string; 
  icon: React.ReactNode; 
  label: string; 
  onClick: () => void;
}) => (
  <Link
    to={to}
    className="flex items-center px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
    onClick={onClick}
  >
    <span className="mr-2">{icon}</span>
    {label}
  </Link>
);

export default UserMenu;